/*
 * pure layout decisions for the responsive tables (shared/responsive_table.jsx). Everything keys off
 * the TABLE CONTAINER's width, never the viewport: Hridaya's sidebar changes the space available.
 */

// Cards below Tailwind `sm` (tailwind.config.js:10, form_layout.css:390); 1024 with the sidebar
// expanded (~720px) stays a table.
export const CARD_BELOW = 640;
// A page scrollbar that appears with the taller cards takes ~15px; the way back needs 16px more.
export const MODE_HYSTERESIS = 16;

export const MODE_TABLE = 'table';
// A table with some fold columns folded into their target cell (labelled, never hidden).
export const MODE_FOLD = 'fold';
export const MODE_CARDS = 'cards';

/*
 * Columns that fold, in the order they fold: fold.order ascending, else the rightmost first.
 * fold.always columns are always folded and take no part in fitting.
 */
export const fold_order = (columns) =>
  columns
    .map((c, i) => ({ c, i }))
    .filter(({ c }) => c.fold && !c.fold.always && c.in_table !== false)
    .sort((a, b) => {
      const oa = a.c.fold.order === undefined ? -a.i : a.c.fold.order;
      const ob = b.c.fold.order === undefined ? -b.i : b.c.fold.order;
      return oa - ob;
    })
    .map(({ c }) => c.key);

/*
 * The layout for a container width: cards below the threshold (by width only); otherwise a table
 * with the fewest folded columns that fits. `level` = how many fold columns (fold_order) are folded;
 * needs[level] = the container width that level was measured to need (0 = not measured, so it is
 * tried). Going back to fewer folds needs the hysteresis margin. Cards above the threshold only as
 * a last resort, when even every fold leaves the row wider than the container.
 */
export const fit_level = (
  width,
  prev = 0,
  { needs = {}, levels = 0, threshold = CARD_BELOW, hysteresis = MODE_HYSTERESIS } = {}
) => {
  const start = prev === null || prev === undefined ? 0 : prev;
  if (!(width > 0)) return start;
  if (start === MODE_CARDS ? width < threshold + hysteresis : width < threshold) return MODE_CARDS;
  const from = start === MODE_CARDS ? levels + 1 : start;
  for (let k = 0; k <= levels; k += 1) {
    if (width >= (needs[k] || 0) + (k < from ? hysteresis : 0)) return k;
  }
  return MODE_CARDS;
};

// The data-mode name of a layout: table, fold or cards.
export const mode_name = (layout) => {
  if (layout === MODE_CARDS) return MODE_CARDS;
  return layout > 0 ? MODE_FOLD : MODE_TABLE;
};

/*
 * Which columns render as table columns at a fold level. A folded column renders inside its
 * `into` column's cell (a labelled value), so its value is never hidden; a `line` column renders
 * as a labelled second line under the row. Unknown width (0) folds nothing.
 */
export const column_placement = (columns, width, level = 0) => {
  const by_key = Object.fromEntries(columns.map((c) => [c.key, c]));
  const folding = new Set(fold_order(columns).slice(0, level > 0 ? level : 0));
  const folds = (c) =>
    !!(c && c.fold && by_key[c.fold.into] && width > 0 && (c.fold.always || folding.has(c.key)));
  // A fold into a column that has itself folded lands in that column's own target (chains).
  const home = (c, seen = new Set()) => {
    let at = c;
    while (folds(at) && !seen.has(at.key)) {
      seen.add(at.key);
      at = by_key[at.fold.into];
    }
    return at.key;
  };
  const inline = [];
  const folded = {};
  // line: { from } columns render as a full-width second line under the row (long text).
  const lines = [];
  columns
    .filter((c) => c.in_table !== false)
    .forEach((c) => {
      if (c.line) {
        lines.push(c);
        return;
      }
      if (!folds(c)) {
        inline.push(c);
        return;
      }
      const target = home(c);
      folded[target] = (folded[target] || []).concat([c]);
    });
  return { inline, folded, lines };
};

const role_of = (c) => c.role || '';

/*
 * The card anatomy: checkbox top-left, line 1 identifier + amount, line 2 status chips and the date,
 * a label/value body, and the actions footer. Columns with in_card: false stay out of the card (a
 * twin column carries their value; dropped_values guards money/status/date/counts).
 * has_title: the caller's cardTitle is line 1 (only when no identifier column takes it), and its
 * hideInCard columns are what it shows; otherwise a hideInCard column goes to the body.
 */
export const card_layout = (columns, has_title = false) => {
  const usable = columns.filter((c) => c.in_card !== false);
  const pick = (role) => usable.filter((c) => role_of(c) === role);
  const identifier =
    pick('identifier')[0] ||
    (has_title ? null : usable.find((c) => !['select', 'actions'].includes(role_of(c))));
  const placed = new Set();
  const take = (list) => {
    list.forEach((c) => placed.add(c.key));
    return list;
  };
  const select = take(pick('select'));
  take(identifier ? [identifier] : []);
  const amount = take(pick('amount').slice(0, 1));
  const line2 = take(usable.filter((c) => ['status', 'date'].includes(role_of(c))));
  const actions = take(pick('actions'));
  const title_shown = has_title && !identifier;
  const body = usable.filter((c) => !placed.has(c.key) && !(title_shown && c.hideInCard));
  return {
    select,
    identifier: identifier || null,
    amount: amount[0] || null,
    line2,
    body,
    actions
  };
};

/*
 * Track lists (§2.3 list-row pattern): columns with `track` render a colgroup and never fold. A track is
 * a width ('34px', '26%') or { compact, full } (container < 880px / >= 880px); no track takes the rest.
 */
export const TRACK_FULL_FROM = 880;
// Track lists turn into cards below this container width (the other lists keep CARD_BELOW).
export const TRACK_CARD_BELOW = 660;

export const is_tracked = (columns) => columns.some((c) => !!c.track);

const track_of = (t) => {
  if (!t) return { compact: '', full: '' };
  if (typeof t === 'string') return { compact: t, full: t };
  return { compact: t.compact || t.full || '', full: t.full || t.compact || '' };
};

// One entry per table column (inline columns only), or null when the list has no tracks.
export const col_tracks = (inline) =>
  is_tracked(inline) ? inline.map((c) => ({ key: c.key, ...track_of(c.track) })) : null;

/*
 * The card of a track list: checkbox, identifier and status on line 1, then every other column as a
 * full-width block in column order, the `line` columns as notes, then the actions footer.
 */
export const track_card_layout = (columns) => {
  const usable = columns.filter((c) => c.in_card !== false);
  const first = (role) => usable.find((c) => role_of(c) === role) || null;
  const select = first('select');
  const identifier = first('identifier');
  const status = first('status');
  const actions = usable.filter((c) => role_of(c) === 'actions');
  const notes = usable.filter((c) => c.line);
  const placed = new Set(
    [select, identifier, status, ...actions, ...notes].filter(Boolean).map((c) => c.key)
  );
  return {
    select,
    identifier,
    status,
    blocks: usable.filter((c) => !placed.has(c.key)),
    notes,
    actions
  };
};

/*
 * Money, counts, dates and statuses render in every layout at every width (Kuldeep, 27 Oct):
 * responsive re-arranges, it never drops a value. A value column is one with an amount/status/date
 * role or right-aligned (numbers).
 */
const VALUE_ROLES = ['amount', 'status', 'date'];
export const is_value_column = (c) => VALUE_ROLES.includes(role_of(c)) || c.align === 'right';

// The value columns a layout would not render at this width (empty = nothing dropped). A cardTitle
// carries its hideInCard columns only when it renders (no identifier column).
export const dropped_values = (columns, layout, width, has_title = false) => {
  const shown = new Set();
  if (layout === MODE_CARDS && is_tracked(columns)) {
    const t = track_card_layout(columns);
    [t.select, t.identifier, t.status, ...t.blocks, ...t.notes, ...t.actions]
      .filter(Boolean)
      .forEach((x) => shown.add(x.key));
  } else if (layout === MODE_CARDS) {
    const c = card_layout(columns, has_title);
    [...c.select, c.identifier, c.amount, ...c.line2, ...c.body, ...c.actions]
      .filter(Boolean)
      .forEach((x) => shown.add(x.key));
    if (has_title && !c.identifier)
      columns.filter((x) => x.hideInCard).forEach((x) => shown.add(x.key));
  } else {
    const p = column_placement(columns, width, layout === MODE_FOLD ? Infinity : layout);
    p.inline.concat(p.lines).forEach((x) => shown.add(x.key));
    Object.values(p.folded).forEach((list) => list.forEach((x) => shown.add(x.key)));
  }
  return columns.filter((c) => is_value_column(c) && !shown.has(c.key)).map((c) => c.key);
};

// Line-2 parts: empty ones drop out, so the card never prints a dangling " · " (CD-92).
export const present_parts = (parts) =>
  parts.filter((p) => p !== null && p !== undefined && p !== false && String(p).trim() !== '');

// Card sort options: every sortable column, both directions; value "key|dir".
// A column's `sorts` ([{ sort_key, label }]) are the sorts of values composed into it (no header of
// their own); they are offered in the sort select at every width.
const sort_entries = (columns) =>
  columns.flatMap((c) => [
    ...(c.sort_key && typeof c.label === 'string' && c.label
      ? [{ sort_key: c.sort_key, label: c.label }]
      : []),
    ...(c.sorts || [])
  ]);

export const has_extra_sorts = (columns) => columns.some((c) => (c.sorts || []).length > 0);

export const card_sort_options = (columns, arrows = { asc: '▲', desc: '▼' }) =>
  sort_entries(columns).flatMap((e) => [
    { value: `${e.sort_key}|asc`, label: `${e.label} ${arrows.asc}` },
    { value: `${e.sort_key}|desc`, label: `${e.label} ${arrows.desc}` }
  ]);

export const parse_card_sort = (value) => {
  const [key, dir] = String(value || '').split('|');
  return key && (dir === 'asc' || dir === 'desc') ? { key, dir } : null;
};

export const card_sort_value = (sort) =>
  sort && sort.key ? `${sort.key}|${sort.dir === 'desc' ? 'desc' : 'asc'}` : '';

// A value in a numeric column: amounts and counts align right; a word ("No change", "Not available")
// reads from the left.
export const value_align = (value) =>
  /^[-−+]?\s*₹?\s*[\d,]+(\.\d+)?$/.test(
    String(value === null || value === undefined ? '' : value).trim()
  )
    ? 'right'
    : 'left';
